import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '../lib/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { 
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import { useToast } from '../components/ui/use-toast';
import { Search, UserPlus, UserCheck, UserX, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { useLocation } from 'wouter';

interface Friend {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  avatar_url?: string;
  role: 'client' | 'agent';
  status: 'pending' | 'accepted' | 'none';
  friendship_id?: string;
  user_id?: string;
  friend_id?: string;
  sender?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role: 'client' | 'agent';
  };
  friendship_user_id?: string | null;
}

export default function Friends() {
  const { auth, isGuest, openAuthPrompt } = useAppContext();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResultsOverride, setSearchResultsOverride] = useState<Record<string, Partial<Friend>>>({});
  const [location, setLocation] = useLocation();

  // On mount, initialize searchQuery from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('search') || '';
    setSearchQuery(search);
  }, []);

  // When searchQuery changes, update the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [searchQuery]);

  // Fetch friends list
  const { data: friends, isLoading: loadingFriends, refetch: refetchFriends } = useQuery<Friend[]>({
    queryKey: ['friends', auth.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('friendships')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          friend:friend_id (
            id,
            name,
            email,
            avatar,
            role
          ),
          sender:user_id (
            id,
            name,
            email,
            avatar,
            role
          )
        `)
        .or(`user_id.eq.${auth.user?.id},friend_id.eq.${auth.user?.id}`);

      if (error) throw error;

      return data.map((item: any) => {
        // Always display the other user, not yourself
        const displayUser = item.friend.id === auth.user?.id ? item.sender : item.friend;
        return {
          ...displayUser,
          status: item.status,
          friendship_id: item.id,
          user_id: item.user_id,
          friend_id: item.friend_id,
          sender: item.sender
        };
      });
    },
    enabled: !!auth.user,
    refetchOnWindowFocus: true,
  });

  // Search users
  const { data: searchResultsData, isLoading: searching, refetch: refetchSearch } = useQuery<Friend[]>({
    queryKey: ['search-users', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return [];
      
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, avatar, role')
        .neq('id', auth.user?.id || '')
        .or(`name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
        .limit(10);

      if (error) throw error;

      // Map avatar to avatar_url for UI compatibility
      const usersWithAvatarUrl = data.map(user => ({
        ...user,
        avatar_url: user.avatar
      }));

      // Get friendship status for each user
      const usersWithStatus = await Promise.all(
        usersWithAvatarUrl.map(async (user) => {
          // Find any friendship row between the current user and the searched user
          const { data: friendship } = await supabase
            .from('friendships')
            .select('status, user_id, friend_id')
            .or(`and(user_id.eq.${auth.user?.id},friend_id.eq.${user.id}),and(user_id.eq.${user.id},friend_id.eq.${auth.user?.id})`)
            .maybeSingle();

          // For testing: Only block add if status is 'pending' or 'accepted'
          const status = (friendship && (friendship.status === 'pending' || friendship.status === 'accepted'))
            ? friendship.status
            : 'none';

          return {
            ...user,
            status,
            friendship_user_id: friendship?.user_id || null
          };
        })
      );

      return usersWithStatus;
    },
    enabled: !!searchQuery.trim() && !!auth.user
  });

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const mergedSearchResults = (searchResultsData || [])
    .map(user => ({
      ...user,
      ...searchResultsOverride[user.id],
    }))
    .filter(user => user.id !== auth.user?.id);

  const handleAddFriend = async (userId: string) => {
    if (userId === auth.user?.id) return; // Prevent adding yourself
    try {
      const { error } = await supabase
        .from('friendships')
        .insert({
          user_id: auth.user?.id,
          friend_id: userId,
          status: 'pending'
        });

      if (error) throw error;

      // Create a notification for the receiver
      const { error: notifError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId, // receiver
          message: `${auth.user?.name} sent you a connection request.`
        });
      if (notifError) throw notifError;

      // Optimistically update the UI to show 'Pending'
      setSearchResultsOverride(prev => ({
        ...prev,
        [userId]: { status: 'pending', friendship_user_id: auth.user?.id }
      }));

      // Refetch friends list so UI updates immediately
      refetchFriends();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send connection request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAcceptFriend = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .or(`user_id.eq.${auth.user?.id},friend_id.eq.${auth.user?.id}`)
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

      if (error) throw error;

      refetchFriends();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to accept connection request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFriend = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .or(`user_id.eq.${auth.user?.id},friend_id.eq.${auth.user?.id}`)
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

      if (error) throw error;

      refetchFriends();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove friend. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Undo connection request from search
  const handleUndoFriendRequestFromSearch = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .match({ user_id: auth.user?.id, friend_id: userId, status: 'pending' });
      if (error) throw error;
      // Optimistically update the UI to show 'Add' again
      setSearchResultsOverride(prev => ({
        ...prev,
        [userId]: { status: 'none', friendship_user_id: null }
      }));
      // Refetch friends list so UI updates immediately
      refetchFriends();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to undo connection request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Decline connection request
  const handleDeclineFriend = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .or(`user_id.eq.${userId},friend_id.eq.${auth.user?.id}`)
        .eq('status', 'pending');

      if (error) throw error;

      refetchFriends();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to decline connection request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="px-4 py-4 max-w-md mx-auto">
        {/* Search Section */}
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)] mb-6">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  if (isGuest) {
                    openAuthPrompt('Create an account or sign in to search for other users.');
                    return;
                  }
                  setSearchQuery(e.target.value)
                }}
                onFocus={() => {
                  if (isGuest) {
                    openAuthPrompt('Create an account or sign in to search for other users.');
                  }
                }}
                className="pl-10"
              />
            </div>

            {/* Search Results */}
            {searchQuery.trim() && (
              <div className="mt-4 space-y-2">
                {searching ? (
                  <div className="text-center text-gray-500">Searching...</div>
                ) : mergedSearchResults && mergedSearchResults.length > 0 ? (
                  mergedSearchResults.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center">
                        <Avatar className="h-10 w-10">
                          {user.avatar_url ? (
                            <AvatarImage src={user.avatar_url} alt={user.name} />
                          ) : null}
                          <AvatarFallback>
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">{user.name}</p>
                          {user.role !== 'client' && (
                            <p className="text-sm text-gray-500">{user.role}</p>
                          )}
                        </div>
                      </div>
                      {user.status === 'none' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddFriend(user.id)}
                          className="text-primary"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLocation(`/profile/${user.id}${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`)}
                        className="ml-2"
                      >
                        View Profile
                      </Button>
                      {user.status === 'pending' && user.friendship_user_id === auth.user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUndoFriendRequestFromSearch(user.id)}
                          className="text-gray-500"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Undo
                        </Button>
                      )}
                      {user.status === 'pending' && user.friendship_user_id !== auth.user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled
                          className="text-gray-500"
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Pending
                        </Button>
                      )}
                      {user.status === 'accepted' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFriend(user.id)}
                          className="text-red-600"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500">No users found</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Friends List */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Connections</h3>
          
          {loadingFriends ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : friends && friends.length > 0 ? (
            <div className="space-y-2">
              {friends.map((friend) => (
                <Card key={friend.id} className="backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div
                        className="flex items-center cursor-pointer hover:bg-blue-50 rounded-lg px-2 -mx-2 transition-colors"
                        onClick={() => setLocation(`/profile/${friend.id}`)}
                      >
                        <Avatar className="h-10 w-10">
                          {friend.avatar ? (
                            <AvatarImage src={friend.avatar} alt={friend.name} />
                          ) : null}
                          <AvatarFallback>
                            {friend.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">{friend.name}</p>
                          {friend.role !== 'client' && (
                            <p className="text-sm text-gray-500">{friend.role}</p>
                          )}
                        </div>
                      </div>
                      {/* Only show Accept and Decline if the current user is the receiver and status is pending */}
                      {friend.status === 'pending' && friend.friend_id === auth.user?.id && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAcceptFriend(friend.id)}
                            className="text-primary"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Accept
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeclineFriend(friend.id)}
                            className="text-red-600"
                          >
                            <UserX className="h-4 w-4 mr-2" />
                            Decline
                          </Button>
                        </div>
                      )}
                      {/* Show Pending if the current user is the sender and status is pending */}
                      {friend.status === 'pending' && friend.user_id === auth.user?.id && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUndoFriendRequestFromSearch(friend.id)}
                            className="text-gray-500"
                          >
                            <UserX className="h-4 w-4 mr-2" />
                            Undo
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="text-gray-500"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Pending
                          </Button>
                        </div>
                      )}
                      {/* Show Message + Remove for accepted friends */}
                      {friend.status === 'accepted' && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLocation(`/messages/${friend.id}`)}
                            className="text-primary"
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Message
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                              >
                                <UserX className="h-4 w-4 mr-2" />
                                Remove
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="max-w-sm p-4">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove connection?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove this person from your connections. You can send a new connection request later.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRemoveFriend(friend.id)}>
                                  Confirm
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">No connections yet</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
} 